import { useCallback, useEffect, useState } from 'react'
import { IAppleUserName } from './social.types'
import { loadExternalScript } from './loadExternalScript'

const APPLE_SCRIPT_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

const generateNonce = (): string => {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export interface IAppleSignInResult {
  idToken: string
  nonce: string
  user?: IAppleUserName
}

export const useAppleSignIn = () => {
  const [isReady, setIsReady] = useState(false)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    let cancelled = false

    loadExternalScript(APPLE_SCRIPT_SRC)
      .then(() => {
        if (!cancelled && window.AppleID) setIsReady(true)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async (): Promise<IAppleSignInResult> => {
    if (!window.AppleID) {
      throw new Error('Apple sign-in is not ready yet')
    }

    const nonce = generateNonce()
    window.AppleID.auth.init({
      clientId: process.env.NEXT_PUBLIC_APPLE_SERVICE_ID ?? '',
      scope: 'name email',
      redirectURI: `${process.env.NEXT_PUBLIC_APP_URL ?? ''}/login`,
      usePopup: true,
      nonce,
    })

    const response = await window.AppleID.auth.signIn()
    return {
      idToken: response.authorization.id_token,
      nonce,
      user: response.user?.name,
    }
  }, [])

  return { isReady, loadError, signIn }
}
